import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Clock, Gift, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { useCart } from "../../../context/CartContext";
import { fmtDate, fmtMoney } from "../../../lib/format";
import { daysLeft, savingPct } from "../../../lib/offers";
import { usePromotions, useShop } from "../hooks";
import { mediaUrl, trackPromo } from "../promoTrack";
import { EmptyState, SkeletonBlock } from "../../../components/common/EmptyState";

const OfferItem = ({ o, it, item, price, lang, t }) => {
  const cart = useCart();
  const normal = it.uom === "piece" ? price.final_net_price / item.units_per_box : price.final_net_price;
  const add = () => {
    cart.setMode(it.item_code, it.uom);
    cart.setQty(it.item_code, Math.max(cart.qtyOf(it.item_code), it.min_qty));
    trackPromo(o.id, "add_to_cart", it.item_code);
    toast.success(t("x.offers.added"));
  };
  return (
    <li className="flex flex-col gap-2 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3" data-testid={`offer-item-${o.id}-${it.item_code}`}>
      <Link to={`/app/products/${encodeURIComponent(it.item_code)}`} className="min-w-0 sm:flex-1"><p className="break-words text-sm font-semibold text-navy">{item[`name_${lang}`]}</p>
        <p className="text-xs text-slate-500">{it.item_code}{it.min_qty > 1 && ` · ${t("x.offers.fromQty", { n: it.min_qty })}`}</p></Link>
      <div className="flex items-center gap-3">
      <div className="text-left sm:text-right"><p className="text-xs text-slate-400 line-through">{fmtMoney(normal)}</p>
        <p className="text-base font-bold tabular-nums text-vblue-ink">{fmtMoney(it.offer_price)} <span className="text-xs font-normal text-slate-500">/ {t(it.uom === "piece" ? "x.uom.piece" : "x.uom.box")}</span></p></div>
      <span className="rounded-full bg-vblue px-2 py-0.5 text-xs font-bold text-white">-{savingPct(it.offer_price, normal)}%</span>
      <button type="button" onClick={add} data-testid={`offer-add-${o.id}-${it.item_code}`} className="ml-auto flex h-11 items-center gap-1.5 rounded-full bg-brand px-4 text-sm font-semibold text-navy"><ShoppingCart className="h-4 w-4" />{t("shop.add")}</button>
      </div>
    </li>
  );
};

const OfferCard = ({ o, shop }) => {
  const { t, lang } = useI18n();
  useEffect(() => {
    trackPromo(o.id, "view");
  }, [o.id]);
  const left = daysLeft(o.end_date);
  const items = (o.items || []).filter((it) => shop.byCode[it.item_code] && shop.priceMap[it.item_code]);
  return (
    <article id={`offer-${o.id}`} className="overflow-hidden rounded-2xl bg-white shadow-sm" data-testid={`offer-card-${o.id}`}>
      {o.image_url && <img src={mediaUrl(o.image_url)} alt="" className="h-36 w-full object-cover" />}
      <div className="space-y-2 p-4">
        <div className="flex flex-wrap items-center gap-2">
          {o.personal && <span className="flex items-center gap-1 rounded-full bg-brand px-2 py-0.5 text-[11px] font-bold text-navy" data-testid={`offer-personal-${o.id}`}><Gift className="h-3 w-3" />{t("x.offers.forYou")}</span>}
          <span className="flex items-center gap-1 text-xs text-slate-500" data-testid={`offer-countdown-${o.id}`}><Clock className="h-3.5 w-3.5" />{left === 0 ? t("x.offers.lastDay") : t("x.offers.daysLeft", { n: left })} · {t("x.offers.until", { d: fmtDate(o.end_date) })}</span>
        </div>
        <h2 className="text-lg font-bold text-navy">{o[`title_${lang}`] || o.title_el}</h2>
        {(o[`text_${lang}`] || o.text_el) && <p className="text-sm text-slate-600">{o[`text_${lang}`] || o.text_el}</p>}
        <ul className="divide-y divide-slate-100">{items.map((it) => <OfferItem key={it.item_code + it.uom} o={o} it={it} item={shop.byCode[it.item_code]} price={shop.priceMap[it.item_code]} lang={lang} t={t} />)}</ul>
      </div>
    </article>
  );
};

export default function Offers() {
  const { t } = useI18n();
  const promos = usePromotions();
  const shop = useShop();
  const list = [...(promos.data || [])].filter((o) => (o.items || []).length).sort((a, b) => Number(b.personal) - Number(a.personal));
  return (
    <div className="space-y-4" data-testid="offers-page">
      <h1 className="text-2xl font-bold text-navy sm:text-3xl">{t("x.offers.title")}</h1>
      {promos.isLoading || shop.isLoading ? <SkeletonBlock className="h-48" /> : !list.length ? <EmptyState icon={Gift} title={t("x.offers.none")} testId="offers-empty" />
        : list.map((o) => <OfferCard key={o.id} o={o} shop={shop} />)}
    </div>
  );
}
